/**
 * /tools/dns-health — public, anonymous (3/day per IP) → signed-in (50/day) → Pro (unlimited).
 * Scans SPF, DKIM, DMARC, DNSSEC, MX, CAA, MTA-STS, TLS-RPT + 4 RBLs in parallel.
 */
import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    ShieldCheck, Search, ArrowLeft, AlertTriangle, CheckCircle2,
    XCircle, Lock, Clock, Server, Mail, Wifi, ArrowRight, Loader2,
} from "lucide-react";
import { postJSON } from "../lib/api";
import { usePageMeta } from "../lib/usePageMeta";
import { ToolPageMeta } from "../components/ToolPageMeta";
import { toast } from "sonner";
import { useAuthStore } from "../state/authStore";

function getAnonId() {
    try {
        let id = localStorage.getItem("cf_anon_session");
        if (!id) { id = "anon-" + crypto.randomUUID(); localStorage.setItem("cf_anon_session", id); }
        return id;
    } catch { return "anon-no-storage"; }
}

const PROGRESS_PHASES = [
    "Resolving authoritative nameservers…",
    "Reading SPF, DKIM, DMARC records…",
    "Checking MTA-STS and TLS-RPT…",
    "Querying 4 mail blacklists…",
    "Computing health score…",
];

const Pill = ({ tone, children }) => {
    const tones = {
        ok:   "bg-accent-mint/10 border-accent-mint/30 text-accent-mint",
        warn: "bg-accent-amber/10 border-accent-amber/30 text-accent-amber",
        err:  "bg-accent-red/10 border-accent-red/30 text-accent-red",
        info: "bg-accent-cyan/10 border-accent-cyan/30 text-accent-cyan",
        dim:  "bg-white/[0.04] border-white/[0.08] text-text-dim",
    };
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10.5px] font-mono uppercase tracking-[0.18em] border ${tones[tone] || tones.dim}`}>
            {children}
        </span>
    );
};

const Section = ({ icon: Icon, title, children, testId }) => (
    <div className="rounded-xl border border-white/[0.06] bg-surface/60 backdrop-blur-md p-5" data-testid={testId}>
        <div className="flex items-center gap-2 mb-3">
            <Icon className="w-4 h-4 text-accent-cyan" />
            <h3 className="font-display font-semibold text-[14.5px]">{title}</h3>
        </div>
        <div className="space-y-2 text-[12.5px] text-text-muted leading-relaxed">{children}</div>
    </div>
);

const ScoreDial = ({ score, grade }) => {
    const r = 56, c = 2 * Math.PI * r;
    const offset = c - (score / 100) * c;
    const stroke = score >= 80 ? "#00FF66" : score >= 60 ? "#FFD100" : "#EF4444";
    return (
        <div className="relative w-40 h-40 mx-auto" data-testid="dns-score-dial">
            <svg viewBox="0 0 140 140" className="w-full h-full -rotate-90">
                <circle cx="70" cy="70" r={r} stroke="rgba(255,255,255,0.06)" strokeWidth="10" fill="none" />
                <circle cx="70" cy="70" r={r} stroke={stroke} strokeWidth="10" fill="none"
                    strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
                    style={{ transition: "stroke-dashoffset 0.8s ease-out" }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-display font-semibold text-5xl tracking-tight" data-testid="dns-score-number">{score}</span>
                <span className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-text-muted mt-1">Grade {grade}</span>
            </div>
        </div>
    );
};

export const DnsHealth = () => {
    const { user } = useAuthStore();
    const [domain, setDomain] = useState("");
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [phase, setPhase] = useState(0);
    const phaseTimer = useRef(null);

    usePageMeta({
        title: "Free DNS Health Check — SPF, DKIM, DMARC & Blacklist Scanner",
        description: "Audit your domain's email security (SPF, DKIM, DMARC), DNSSEC, MTA-STS, and 4 major mail blacklists in one report. Free, no signup required for 3 daily checks.",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "DNS Health Check",
            "applicationCategory": "SecurityApplication",
            "operatingSystem": "Web",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
            "description": "Free DNS, SPF, DKIM, DMARC, DNSSEC, MTA-STS and mail blacklist scanner."
        },
    });

    // Page-view telemetry once
    useEffect(() => {
        postJSON("/tools/usage", {
            tool_id: "dns-health-check", action: "page_view", anon_session_id: getAnonId(),
        }).catch(() => {});
    }, []);

    const cycle = () => {
        phaseTimer.current = setInterval(() => setPhase((p) => (p + 1) % PROGRESS_PHASES.length), 1200);
    };
    const stopCycle = () => { if (phaseTimer.current) clearInterval(phaseTimer.current); phaseTimer.current = null; };

    const normalize = (raw) => raw.trim().toLowerCase().replace(/^https?:\/\//, "").split("/")[0].replace(/^\./, "").replace(/\.$/, "");

    const submit = async (e) => {
        e?.preventDefault();
        const d = normalize(domain);
        if (!/^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/.test(d)) {
            toast.error("Enter a valid domain like example.com");
            return;
        }
        setBusy(true); setError(null); setResult(null); setPhase(0); cycle();
        try {
            const res = await postJSON("/tools/dns-health", { domain: d });
            setResult(res);
        } catch (err) {
            // Try parse rate-limit payload
            if (err?.payload?.detail?.error === "rate_limited") {
                setError({ kind: "rate_limited", ...err.payload.detail });
            } else if (err?.status === 429) {
                setError({ kind: "rate_limited", message: err.message });
            } else {
                setError({ kind: "error", message: err?.message || "Lookup failed" });
            }
        } finally {
            setBusy(false); stopCycle();
        }
    };

    return (
        <div className="pt-24 sm:pt-32 pb-20 mx-auto max-w-5xl px-5" data-testid="dns-health-page">
            <ToolPageMeta toolId="dns-health" />
            <Link to="/tools" className="inline-flex items-center gap-1.5 text-[12.5px] text-text-muted hover:text-accent-cyan mb-6">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to tools
            </Link>

            <motion.header initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-7">
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">Free tool</p>
                <h1 className="mt-2 font-display font-semibold text-3xl sm:text-4xl tracking-tight flex items-center gap-3">
                    <ShieldCheck className="w-7 h-7 text-accent-cyan" /> DNS Health Check
                </h1>
                <p className="mt-3 text-[14px] text-text-muted max-w-2xl">
                    Audit your domain's email security and DNS hygiene in 10 seconds — SPF, DKIM, DMARC, DNSSEC, MTA-STS, plus 4 major mail blacklists. Free for up to {user ? "50" : "3"} checks per day.
                </p>
            </motion.header>

            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 mb-8" data-testid="dns-form">
                <input
                    type="text" value={domain} onChange={(e) => setDomain(e.target.value)}
                    placeholder="example.com" autoFocus
                    data-testid="dns-input"
                    className="flex-1 px-4 py-3 bg-canvas border border-white/[0.08] rounded-md text-[16px] text-text-primary font-mono placeholder:text-text-dim focus:outline-none focus:border-accent-cyan focus:shadow-glow-cyan"
                />
                <button type="submit" disabled={busy || !domain.trim()} data-testid="dns-submit"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-accent-cyan text-canvas text-[13.5px] font-semibold hover:shadow-glow-cyan disabled:opacity-50 transition-all">
                    {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</> : <><Search className="w-4 h-4" /> Run check</>}
                </button>
            </form>

            {busy && (
                <div className="rounded-md border border-accent-cyan/20 bg-accent-cyan/[0.04] p-4 text-[12.5px] font-mono text-accent-cyan flex items-center gap-2" data-testid="dns-progress">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>{PROGRESS_PHASES[phase]}</span>
                </div>
            )}

            {error?.kind === "rate_limited" && (
                <div className="rounded-xl border border-accent-amber/30 bg-accent-amber/[0.06] p-6" data-testid="dns-rate-limit">
                    <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-accent-amber shrink-0 mt-0.5" />
                        <div>
                            <h3 className="font-display font-semibold text-[15px]">Free daily limit reached</h3>
                            <p className="mt-2 text-[13px] text-text-muted">
                                You've used your 3 free anonymous checks today. {error.signup_unlocks ? "Sign up free for 50 checks per day, or upgrade to Pro for unlimited." : "Try again later or upgrade to Pro."}
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {!user && <Link to="/signup" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-cyan text-canvas text-[12.5px] font-medium hover:shadow-glow-cyan">Sign up free</Link>}
                                <Link to="/pricing" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-accent-amber/40 text-accent-amber text-[12.5px] font-medium hover:bg-accent-amber/10">Upgrade to Pro</Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {error?.kind === "error" && (
                <div className="rounded-md border border-accent-red/30 bg-accent-red/[0.04] p-4 text-[12.5px] text-accent-red font-mono">
                    <AlertTriangle className="inline w-3.5 h-3.5 mr-1.5" /> {error.message}
                </div>
            )}

            <AnimatePresence>
                {result && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="dns-results">
                        {/* Top score card */}
                        <div className="rounded-2xl border border-white/[0.06] bg-surface/70 backdrop-blur-md p-7 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                            <ScoreDial score={result.score} grade={result.grade} />
                            <div className="md:col-span-2">
                                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-text-dim">{result.domain}</p>
                                <h2 className="mt-2 font-display font-semibold text-2xl tracking-tight">{result.summary}</h2>
                                <p className="mt-2 text-[11.5px] font-mono text-text-dim">Checked {new Date(result.checked_at).toLocaleString()}</p>
                            </div>
                        </div>

                        {/* Sections grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Section icon={Mail} title="Email security" testId="section-email">
                                <div className="flex items-center justify-between"><span>SPF</span>{result.sections.spf.present ? <Pill tone={result.sections.spf.all_policy === "hard_fail" ? "ok" : "warn"}>{result.sections.spf.all_policy?.replace("_", " ")}</Pill> : <Pill tone="err">missing</Pill>}</div>
                                <div className="flex items-center justify-between"><span>DMARC</span>{result.sections.dmarc.present ? <Pill tone={result.sections.dmarc.policy === "reject" ? "ok" : result.sections.dmarc.policy === "quarantine" ? "warn" : "err"}>p={result.sections.dmarc.policy}</Pill> : <Pill tone="err">missing</Pill>}</div>
                                <div className="flex items-center justify-between"><span>DKIM selectors</span>{result.sections.dkim.selectors_found?.length ? <Pill tone="ok">{result.sections.dkim.selectors_found.join(", ")}</Pill> : <Pill tone="err">none found</Pill>}</div>
                            </Section>

                            <Section icon={Server} title="Mail infrastructure" testId="section-mail">
                                <div className="flex items-center justify-between"><span>MX records</span><Pill tone={result.sections.mx.length ? "ok" : "err"}>{result.sections.mx.length}</Pill></div>
                                {result.sections.mx.slice(0, 3).map((m) => (
                                    <div key={m.exchange} className="font-mono text-[10.5px] text-text-dim">{m.priority} · {m.exchange}</div>
                                ))}
                                {result.sections.reverse_dns && <div className="flex items-center justify-between"><span>Reverse DNS</span><span className="font-mono text-[10.5px]">{result.sections.reverse_dns}</span></div>}
                            </Section>

                            <Section icon={Wifi} title="DNS infrastructure" testId="section-dns">
                                <div className="flex items-center justify-between"><span>A records</span><Pill tone={result.sections.a.length ? "ok" : "err"}>{result.sections.a.length}</Pill></div>
                                <div className="flex items-center justify-between"><span>AAAA (IPv6)</span><Pill tone={result.sections.aaaa.length ? "ok" : "dim"}>{result.sections.aaaa.length}</Pill></div>
                                <div className="flex items-center justify-between"><span>Nameservers</span><Pill tone={result.sections.ns.length ? "ok" : "err"}>{result.sections.ns.length}</Pill></div>
                                <div className="flex items-center justify-between"><span>DNSSEC</span>{result.sections.dnssec.signed ? <Pill tone="ok">signed</Pill> : <Pill tone="warn">not signed</Pill>}</div>
                                <div className="flex items-center justify-between"><span>CAA</span><Pill tone={result.sections.caa.length ? "ok" : "dim"}>{result.sections.caa.length}</Pill></div>
                            </Section>

                            <Section icon={Lock} title="Modern standards" testId="section-modern">
                                <div className="flex items-center justify-between"><span>MTA-STS</span>{result.sections.mta_sts.present ? <Pill tone="ok">enabled</Pill> : <Pill tone="dim">not configured</Pill>}</div>
                                <div className="flex items-center justify-between"><span>TLS-RPT</span>{result.sections.tls_rpt.present ? <Pill tone="ok">enabled</Pill> : <Pill tone="dim">not configured</Pill>}</div>
                            </Section>

                            <Section icon={ShieldCheck} title="Mail blacklists" testId="section-rbls">
                                {result.sections.blacklists.map((b) => (
                                    <div key={b.rbl} className="flex items-center justify-between">
                                        <span className="font-mono text-[11px]">{b.rbl}</span>
                                        {b.skipped ? <Pill tone="dim">skipped</Pill> : b.listed ? <Pill tone="err">listed</Pill> : <Pill tone="ok">clean</Pill>}
                                    </div>
                                ))}
                            </Section>

                            <Section icon={CheckCircle2} title="Score breakdown" testId="section-score">
                                <p className="leading-relaxed">{result.summary}</p>
                                <p className="text-[11px] text-text-dim mt-2">Final grade <span className="font-mono text-accent-cyan">{result.grade}</span>. Higher is better.</p>
                            </Section>
                        </div>

                        {/* Recommendations */}
                        {result.recommendations.length > 0 && (
                            <section className="rounded-xl border border-white/[0.06] bg-surface/60 p-5" data-testid="dns-recommendations">
                                <h3 className="font-display font-semibold text-[15px] mb-3 flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-accent-amber" /> Recommendations
                                </h3>
                                <div className="space-y-2">
                                    {result.recommendations.slice(0, 7).map((r, i) => (
                                        <div key={i} className="flex items-start gap-3 p-3 rounded-md border border-white/[0.04] bg-canvas/40">
                                            <Pill tone={r.priority === "high" ? "err" : r.priority === "medium" ? "warn" : "info"}>{r.priority}</Pill>
                                            <div className="flex-1">
                                                <p className="text-[13px] font-medium text-text-primary">{r.title}</p>
                                                <p className="text-[12px] text-text-muted mt-1 leading-relaxed">{r.rationale}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Save report CTA */}
                        {!user && (
                            <div className="rounded-xl border border-accent-cyan/15 bg-accent-cyan/[0.04] p-5 flex items-center justify-between flex-wrap gap-3">
                                <div>
                                    <p className="font-display font-semibold text-[14.5px]">Want to save this report?</p>
                                    <p className="text-[12.5px] text-text-muted mt-1">Sign up free to save up to 50 reports per day with PDF export.</p>
                                </div>
                                <Link to={`/signup?next=/tools/dns-health`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-cyan text-canvas text-[13px] font-medium hover:shadow-glow-cyan">
                                    Sign up free <ArrowRight className="w-3.5 h-3.5" />
                                </Link>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* FAQ (SEO) */}
            <section className="mt-12">
                <h2 className="font-display font-semibold text-2xl tracking-tight mb-5">Email security FAQ</h2>
                <div className="space-y-2">
                    <details className="rounded-md border border-white/[0.04] bg-surface/40 p-4 [&_summary::-webkit-details-marker]:hidden">
                        <summary className="cursor-pointer text-[13.5px] font-semibold">What is SPF and why does it matter?</summary>
                        <p className="mt-3 text-[13px] text-text-muted leading-relaxed">SPF (Sender Policy Framework) is a DNS TXT record listing which IP addresses are authorized to send mail for your domain. Without SPF, anyone on the internet can trivially forge mail "From: you@yourdomain.com" — a tactic called domain spoofing that drives most business-email-compromise (BEC) fraud. A properly configured SPF ending in <code>-all</code> (hard-fail) tells receiving servers to discard mail that does not come from your approved IPs.</p>
                    </details>
                    <details className="rounded-md border border-white/[0.04] bg-surface/40 p-4 [&_summary::-webkit-details-marker]:hidden">
                        <summary className="cursor-pointer text-[13.5px] font-semibold">What does the DMARC policy mean?</summary>
                        <p className="mt-3 text-[13px] text-text-muted leading-relaxed">DMARC sits on top of SPF and DKIM. The <code>p=</code> tag tells receivers what to do when a message fails both checks: <code>none</code> = monitor only, <code>quarantine</code> = junk folder, <code>reject</code> = discard outright. Best practice is to roll out at <code>p=none</code> for 2-4 weeks to gather aggregate reports (the <code>rua</code> address), confirm no legitimate flows fail, then move to quarantine, then to reject.</p>
                    </details>
                    <details className="rounded-md border border-white/[0.04] bg-surface/40 p-4 [&_summary::-webkit-details-marker]:hidden">
                        <summary className="cursor-pointer text-[13.5px] font-semibold">My domain has no DKIM — what should I do?</summary>
                        <p className="mt-3 text-[13px] text-text-muted leading-relaxed">DKIM (DomainKeys Identified Mail) cryptographically signs outbound mail headers so that any tampering is detectable. Most modern mail providers (Google Workspace, Microsoft 365, Postmark, SendGrid) walk you through DKIM key generation in their admin console — you copy a CNAME or TXT into your DNS and you're done. Your selectors are typically <code>google._domainkey</code>, <code>selector1._domainkey</code>, etc. — our scanner checks the most common ones automatically.</p>
                    </details>
                    <details className="rounded-md border border-white/[0.04] bg-surface/40 p-4 [&_summary::-webkit-details-marker]:hidden">
                        <summary className="cursor-pointer text-[13.5px] font-semibold">Why does the health score matter?</summary>
                        <p className="mt-3 text-[13px] text-text-muted leading-relaxed">A higher score correlates with both better deliverability (your legitimate mail reaches inboxes) and lower attack surface (criminals find it harder to impersonate you). Domains scoring below 60 typically have at least one of: missing DMARC, missing DKIM, IP on an RBL, or no DNSSEC — each of which is exploitable. The score is intentionally weighted toward email security because that's where most production impact lives.</p>
                    </details>
                </div>
            </section>
        </div>
    );
};

export default DnsHealth;
